import React, { useEffect, useRef } from "react";

const Fireworks = ({ duration = 2500, onDone }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef();
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    let w = window.innerWidth;
    let h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    // helpers
    const rand = (min, max) => Math.random() * (max - min) + min;

    const spawnBurst = (x, y, count = 70) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = rand(2, 6);
        particlesRef.current.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: rand(800, 1400),  // ms
          age: 0,
          size: rand(1.5, 3.5),
        });
      }
    };

    // spawn a few bursts from random positions near top/middle
    const bursts = 5;
    for (let i = 0; i < bursts; i++) {
      const bx = rand(w * 0.2, w * 0.8);
      const by = rand(h * 0.2, h * 0.5);
      setTimeout(() => spawnBurst(bx, by), i * 220);
    }

    let last = performance.now();
    const gravity = 0.045;
    const fade = 0.006;

    const loop = (t) => {
      const dt = t - last;
      last = t;

      ctx.clearRect(0, 0, w, h);

      particlesRef.current = particlesRef.current.filter(p => {
        p.age += dt;
        p.vy += gravity;      // gravity
        p.x += p.vx;
        p.y += p.vy;

        const alpha = Math.max(0, 1 - p.age * fade / p.life);

        if (alpha <= 0) return false;

        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = "white"; // let the browser choose default color scheme
        ctx.fill();
        ctx.globalAlpha = 1;

        return p.age < p.life;
      });

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    // stop after duration
    const endTimer = setTimeout(() => {
      cancelAnimationFrame(rafRef.current);
      onDone?.();
    }, duration);

    const handleResize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(endTimer);
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [duration, onDone]);

  return (
    <canvas
      ref={canvasRef}
      className="fireworks-overlay"
      aria-hidden
    />
  );
};

export default Fireworks;
